import { faro } from '@grafana/faro-web-sdk';
import { type ReactNode, useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { RouteErrorBoundary } from './components/error/route-error-boundary';
import FormPage from './pages/form';
import { FormPrintPage } from './pages/form-print';
import { FormsManagementPage } from './pages/forms-management';
import { GraphicsPage } from './pages/graphics';
import { LabelsPage } from './pages/labels';
import { LocationsPage } from './pages/locations';
import { NotFoundPage } from './pages/not-found';
import { PrintPage } from './pages/print';
import { PrintJobsPage } from './pages/print-jobs';
import { PrintersPage } from './pages/printers';
import { ServersPage } from './pages/servers';
import { useUserStore } from './stores/user-store';
import { isAdmin, isOperator } from './utils/permissions';

const RootRoute = () => {
  const { user } = useUserStore();
  return isOperator(user) ? <Navigate to="/form" replace /> : <PrintPage />;
};

const NonOperatorRoute = ({ children }: { children: ReactNode }) => {
  const { user } = useUserStore();
  return isOperator(user) ? <Navigate to="/form" replace /> : children;
};

const AdminRoute = ({ children }: { children: ReactNode }) => {
  const { user } = useUserStore();
  return isAdmin(user) ? children : <Navigate to="/" replace />;
};

// Reports the current route to Faro as a "view", so every signal emitted while on this route
// (errors, logs, traces, navigation spans) can be filtered/grouped by page in Grafana.
const FaroViewTracker = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    faro.api.setView({ name: pathname });
  }, [pathname]);

  return null;
};

export const AppRoutes = () => (
  <>
    <FaroViewTracker />
    <Routes>
      <Route path="/" element={<RootRoute />} errorElement={<RouteErrorBoundary />} />
      <Route path="/printers" element={<PrintersPage />} errorElement={<RouteErrorBoundary />} />
      <Route path="/labels" element={<LabelsPage />} errorElement={<RouteErrorBoundary />} />
      <Route path="/graphics" element={<GraphicsPage />} errorElement={<RouteErrorBoundary />} />
      <Route
        path="/locations"
        element={
          <AdminRoute>
            <LocationsPage />
          </AdminRoute>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route path="/print-jobs" element={<PrintJobsPage />} errorElement={<RouteErrorBoundary />} />
      <Route path="/servers" element={<ServersPage />} errorElement={<RouteErrorBoundary />} />
      <Route path="/form" element={<FormsManagementPage />} errorElement={<RouteErrorBoundary />} />
      <Route
        path="/form/new"
        element={
          <NonOperatorRoute>
            <FormPage />
          </NonOperatorRoute>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route path="/form-print" element={<FormPrintPage />} errorElement={<RouteErrorBoundary />} />
      <Route
        path="/operator-access"
        element={
          <NonOperatorRoute>
            <FormsManagementPage />
          </NonOperatorRoute>
        }
        errorElement={<RouteErrorBoundary />}
      />
      <Route path="*" element={<NotFoundPage />} errorElement={<RouteErrorBoundary />} />
    </Routes>
  </>
);
